import type { TFunction } from "i18next";
import {
  BarChart2,
  BookCopy,
  BookOpen,
  Calculator,
  CircleHelp,
  FileText,
  Megaphone,
  Plug,
  type LucideIcon,
} from "lucide-react";
import type { Domain } from "@appcaire/ui";

export type ResourceCategory = {
  title: string;
  description: string;
  icon: LucideIcon;
  routeKey: string;
  domain: Domain;
};

export type ResourceGroup = {
  id: string;
  eyebrow: string;
  title: string;
  domain: Domain;
  categories: ResourceCategory[];
};

/** Resource landing and Blog overview follow the public menu's hierarchy. */
export function getBlogResourceCategories(en: boolean): ResourceCategory[] {
  return [
    {
      title: en ? "Articles" : "Artiklar",
      description: en
        ? "Ideas and insights on AI and home care."
        : "Perspektiv och insikter om AI och hemtjänst.",
      icon: FileText,
      routeKey: "resourcesCategory.articles",
      domain: "society",
    },
    {
      title: en ? "Guides" : "Guider",
      description: en
        ? "Practical guides to planning, staffing and change."
        : "Praktiska guider till planering, bemanning och förändring.",
      icon: BookCopy,
      routeKey: "resourcesCategory.guides",
      domain: "planning",
    },
    {
      title: en ? "Comparisons" : "Jämförelser",
      description: en
        ? "Compare approaches and planning solutions."
        : "Jämför arbetssätt och planeringslösningar.",
      icon: BarChart2,
      routeKey: "resourcesCategory.comparisons",
      domain: "attention",
    },
    {
      title: en ? "News" : "Nyheter",
      description: en
        ? "Product updates and new ways of working."
        : "Produktnyheter och nya arbetssätt.",
      icon: Megaphone,
      routeKey: "resourcesCategory.news",
      domain: "care",
    },
  ];
}

export function getResourceGroups(_t: TFunction, en: boolean): ResourceGroup[] {
  return [
    {
      id: "resources-overview",
      eyebrow: en ? "Resources" : "Resurser",
      title: en ? "Find the right resource" : "Hitta rätt resurs",
      domain: "society",
      categories: [
        {
          title: en ? "Blog" : "Blogg",
          description: en
            ? "Articles, guides, comparisons and news."
            : "Artiklar, guider, jämförelser och nyheter.",
          icon: FileText,
          routeKey: "resourcesCategory.blog",
          domain: "planning",
        },
        {
          title: en ? "Help" : "Hjälp",
          description: en
            ? "Step-by-step help for using Caire."
            : "Hjälp steg för steg för dig som använder Caire.",
          icon: BookOpen,
          routeKey: "help.index",
          domain: "human",
        },
        {
          title: en
            ? "Developers & integrations"
            : "Utvecklare & integrationer",
          description: en
            ? "Connect API, Swagger, MCP and architecture."
            : "Connect API, Swagger, MCP och arkitektur.",
          icon: Plug,
          routeKey: "resourcesCategory.developers",
          domain: "society",
        },
        {
          title: en ? "FAQ" : "Vanliga frågor",
          description: en
            ? "Answers about the platform, data and pricing."
            : "Svar om plattformen, data och priser.",
          icon: CircleHelp,
          routeKey: "faq",
          domain: "human",
        },
        {
          title: en ? "Calculator" : "Kalkylator",
          description: en
            ? "Explore efficiency, continuity and savings."
            : "Räkna på effektivitet, kontinuitet och besparing.",
          icon: Calculator,
          routeKey: "calculator",
          domain: "care",
        },
      ],
    },
  ];
}
